import { useCallback, useEffect, useState } from 'react';

import { useLatestRef } from '@clutter/hooks';

import { PlanName } from '@root/resources/types/plan_name';

/**
 * Keeps the plan slider responsive while deferring flow-state updates until the
 * user releases the thumb. That avoids StorageBundles, Availabilities, and
 * pricing refetches on every tick while dragging.
 */
export const useSliderPlanSize = (
  planSize: PlanName | undefined,
  onPlanSizeChange: (plan: PlanName) => void,
) => {
  const [displayPlanSize, setDisplayPlanSize] = useState(planSize);
  const displayPlanSizeRef = useLatestRef(displayPlanSize);
  const onPlanSizeChangeRef = useLatestRef(onPlanSizeChange);
  const committedPlanSizeRef = useLatestRef(planSize);

  useEffect(() => {
    setDisplayPlanSize(planSize);
  }, [planSize]);

  const setPlanSizeFromSlider = useCallback((next: PlanName) => {
    setDisplayPlanSize(next);
  }, []);

  const setPlanSizeImmediate = useCallback(
    (next: PlanName) => {
      setDisplayPlanSize(next);
      onPlanSizeChangeRef.current(next);
    },
    [onPlanSizeChangeRef],
  );

  const commitPlanSize = useCallback(() => {
    const current = displayPlanSizeRef.current;
    if (!current || current === committedPlanSizeRef.current) {
      return;
    }
    onPlanSizeChangeRef.current(current);
  }, [committedPlanSizeRef, displayPlanSizeRef, onPlanSizeChangeRef]);

  return {
    displayPlanSize,
    setPlanSizeFromSlider,
    setPlanSizeImmediate,
    commitPlanSize,
  };
};
